import styled from "@emotion/styled";
import { type CoworkerAndTeamSelectionMode, useMyTeams } from "@repo/api";
import {
  Accordion,
  AccordionItem,
  ListView,
  ListViewItem,
  Text,
  peopleIcon,
  personIcon,
} from "@repo/skapa";
import { t } from "@repo/translation";
import { getAbbreviationIndex, maskEmail } from "@repo/utils";

import { useSettings } from "../../settings/SettingsProvider";
import { CoWorkerName } from "../CoWorkerName";
import { ElementsContainer } from "../ElementsContainer";
import { type Action, type ModalState, isTeamSelection } from "./reducer";
import {
  applyMemberToggle,
  isMultiCoworkerSelection,
  isSingleCoworkerSelection,
  showOnlyTeamSelection,
  showTeamSelection,
} from "./selectionMode";

const SkapaOverride = styled.div`
  .saleslead__accordion__content {
    padding-top: 0;
  }

  .saleslead__accordion-item-header {
    padding-bottom: 0;
  }
`;

interface Props {
  selectionMode: CoworkerAndTeamSelectionMode;
  dispatch: React.Dispatch<Action>;
  state: ModalState;
}

export const CoworkerSelection = ({
  selectionMode,
  state,
  dispatch,
}: Props) => {
  const { customerMode } = useSettings();
  const { data: myTeams } = useMyTeams();

  return (
    <SkapaOverride>
      <ElementsContainer>
        <Text textStyle="Heading.S">{t("label.my_teams")}</Text>
        <Accordion>
          {myTeams?.map((team) => (
            <AccordionItem
              key={`my-team-${team.teamId}`}
              id={`my-team-${team.teamId}`}
              title={team.teamName}
              caption={
                team.description
                  ? `${team.description} | ${t("label.ID")}: ${team.teamId}`
                  : `${t("label.ID")}: ${team.teamId}`
              }
              open={myTeams.length === 1 ? true : undefined}
            >
              <ListView
                key={`my-team-${team.teamId}`}
                id={`coworker-select-list-${team.teamId}`}
              >
                {showOnlyTeamSelection(selectionMode) && (
                  <ListViewItem
                    leadingIcon={peopleIcon}
                    title={t("action.select_only_team")}
                    description={t("prompt.describe_only_team")}
                    control="radiobutton"
                    controlProps={{
                      value: String(team.teamId),
                      checked:
                        isTeamSelection(state.selected) &&
                        state.selected.assignedToTeam === true &&
                        state.selected.team.teamId === team.teamId,
                      onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                        const newValue = e.target.checked ? team : undefined;
                        dispatch({
                          type: "SET_TEAM",
                          payload: {
                            team: newValue,
                            assignedToTeam: true,
                            members: [],
                          },
                        });
                      },
                    }}
                  />
                )}
                {showTeamSelection(selectionMode) && (
                  <ListViewItem
                    leadingIcon={peopleIcon}
                    title={t("action.select_team")}
                    description={t("prompt.describe_select_team")}
                    control="radiobutton"
                    controlProps={{
                      value: String(team.teamId),
                      checked:
                        isTeamSelection(state.selected) &&
                        !state.selected.assignedToTeam &&
                        state.selected.team.teamId === team.teamId,
                      onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                        const newValue = e.target.checked ? team : undefined;
                        dispatch({
                          type: "SET_TEAM",
                          payload: {
                            team: newValue,
                            assignedToTeam: false,
                            members: team.teamMembers.map((m) => ({
                              ...m,
                              selected: true,
                            })),
                          },
                        });
                      },
                    }}
                  />
                )}
                {team.teamMembers.map((member) => (
                  <ListViewItem
                    key={member.emailId}
                    leadingIcon={personIcon}
                    title={
                      <CoWorkerName
                        email={member.emailId}
                        emails={team.teamMembers.map((m) => m.emailId)}
                        textStyle="Heading.XS"
                      />
                    }
                    description={
                      customerMode
                        ? maskEmail(
                            member.emailId,
                            getAbbreviationIndex(
                              member.emailId,
                              team.teamMembers.map((m) => m.emailId)
                            )
                          )
                        : member.emailId
                    }
                    control={
                      isSingleCoworkerSelection(selectionMode)
                        ? "radiobutton"
                        : "checkbox"
                    }
                    controlProps={{
                      value: member.emailId,
                      checked:
                        (isTeamSelection(state.selected) &&
                          !state.selected.assignedToTeam &&
                          state.selected.team.teamId === team.teamId &&
                          Boolean(
                            state.selected.members.find(
                              (m) => m.emailId === member.emailId && m.selected
                            )
                          )) ||
                        (!isTeamSelection(state.selected) &&
                          Array.isArray(state.selected) &&
                          Boolean(
                            state.selected.find(
                              (el) => el.emailId === member.emailId
                            )
                          )),
                      onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                        if (isTeamSelection(state.selected)) {
                          dispatch(
                            applyMemberToggle(
                              state.selected,
                              selectionMode,
                              member.emailId
                            )
                          );
                          return;
                        }

                        const existing = Array.isArray(state.selected)
                          ? state.selected
                          : [];
                        const newValue = e.target.checked
                          ? isMultiCoworkerSelection(selectionMode) ||
                            showTeamSelection(selectionMode)
                            ? [...existing, member]
                            : [member]
                          : existing.filter(
                              (m) => m.emailId !== member.emailId
                            );

                        dispatch({
                          type: "SET_COWORKERS",
                          payload: newValue,
                        });
                      },
                    }}
                  />
                ))}
              </ListView>
            </AccordionItem>
          ))}
        </Accordion>
      </ElementsContainer>
    </SkapaOverride>
  );
};
